/*
 * RemindersModal — opens from a student or teacher profile.
 *
 * Layout: left side = sectioned table preview (Upcoming, Past); right side =
 * ReminderForm for creating a new task. Linked-user is locked to the profile
 * the modal was opened from.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  FaRegBell,
  FaCheckSquare,
  FaRegSquare,
  FaClock,
  FaGlobe,
  FaPlus,
  FaTimes,
  FaRegEdit,
  FaRegStickyNote,
  FaRegTrashAlt,
  FaBuilding
} from 'react-icons/fa';

import CustomModal from '../../util_components/custom_modal';
import ConfirmationPopup from '../../util_components/confirmation_popup';
import { getCookie } from '../../../util_functions';
import ReminderForm from './ReminderForm';
import ReminderNotes from './ReminderNotes';
import ReminderDetailsModal from './ReminderDetailsModal';
import PriorityIcon from './PriorityIcon';
import TaskTooltipContent from './TaskTooltipContent';
import './reminders.css';
import {
  formatRelativeDue,
  isPastDue,
  splitTasksUpcomingPast,
  taskTooltipStyle
} from './constants';
import {
  list_tasks,
  create_task,
  update_task,
  delete_task,
  label_by_uuid,
  department_by_uuid,
  current_team_member,
  prime_team_members
} from './api';

function TaskRow({ task, onToggleComplete, onEdit, onViewNotes, onDelete, isEditing }) {
  const overdue = isPastDue(task);
  const labels = (task.label_uuids || []).map(label_by_uuid).filter(Boolean);
  const department = department_by_uuid(task.department_uuid);
  return (
    <div
      className={`group flex items-start gap-2.5 border-b border-gray-100 px-3 py-2.5 transition hover:bg-gray-50 ${
        overdue ? 'bg-state-error-bg-subtle' : ''
      } ${isEditing ? 'bg-mytPrimary/5 ring-1 ring-inset ring-mytPrimary/30' : ''}`}
    >
      <button
        type="button"
        onClick={() => onToggleComplete(task)}
        data-tooltip-id="reminders-tooltip"
        data-tooltip-content={task.status === 'completed' ? 'Mark as pending' : 'Mark as done'}
        aria-label={task.status === 'completed' ? 'Mark as pending' : 'Mark as done'}
        className="reminders-icon-btn mt-0.5"
      >
        {task.status === 'completed' ? (
          <FaCheckSquare className="text-green-600" size={16} />
        ) : (
          <FaRegSquare size={16} />
        )}
      </button>
      <div className="mt-0.5 shrink-0">
        <PriorityIcon priority={task.priority} size={24} />
      </div>
      <div className="min-w-0 flex-1">
        {/*
          Title is a button so clicking it opens the notes/details modal —
          matches what clicking the FaRegStickyNote icon below does, and lets
          operators jump into the row from the headline they're already
          reading. Styled to look like plain text (no padding/border/bg);
          rich tooltip is still wired via data-tooltip-* on this element.
        */}
        <button
          type="button"
          onClick={() => onViewNotes(task)}
          data-tooltip-id="reminders-task-tooltip"
          data-tooltip-content={task.uuid}
          className={`block w-full cursor-pointer truncate border-0 bg-transparent p-0 text-left text-sm ${
            task.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-800'
          }`}
        >
          {task.title}
        </button>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-gray-500">
          {/* Assignee/linked-user are intentionally omitted here — the modal
              header already names the linked user, and "who's doing the task"
              is rarely the focal information in this scoped view. */}
          <span
            className={`inline-flex items-center gap-1.5 ${overdue ? 'text-red-600 font-medium' : ''}`}
          >
            <FaClock size={18} /> {formatRelativeDue(task)}
          </span>
          {labels.map((l) => (
            <span
              key={l.uuid}
              className="rounded-full px-1.5 py-0.5 text-[10px] text-gray-700"
              style={{ background: l.color }}
            >
              {l.name}
            </span>
          ))}
          {department ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] text-gray-700"
              style={{ background: department.color }}
              title={`Department: ${department.name}`}
            >
              <FaBuilding size={7} className="opacity-50" />
              {department.name}
            </span>
          ) : null}
        </div>
      </div>
      <div
        className={`flex items-center gap-0.5 transition ${
          isEditing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
      >
        {/* FaRegEdit has more whitespace inside its viewBox than FaRegClone,
            so we bump it up to 18 to keep the two visually balanced. */}
        <button
          type="button"
          onClick={() => onEdit(task)}
          data-tooltip-id="reminders-tooltip"
          data-tooltip-content="Edit this reminder"
          aria-label="Edit reminder"
          className={`reminders-icon-btn ${isEditing ? 'text-mytPrimary' : ''}`}
        >
          <FaRegEdit size={18} />
        </button>
        <button
          type="button"
          onClick={() => onViewNotes(task)}
          data-tooltip-id="reminders-tooltip"
          data-tooltip-content={`View details, notes${task.notes?.length ? ` (${task.notes.length})` : ''}`}
          aria-label="View notes"
          className="reminders-icon-btn"
        >
          <FaRegStickyNote size={16} />
        </button>
        <button
          type="button"
          onClick={() => onDelete(task)}
          data-tooltip-id="reminders-tooltip"
          data-tooltip-content="Delete this reminder"
          aria-label="Delete reminder"
          className="reminders-icon-btn"
        >
          <FaRegTrashAlt size={15} />
        </button>
      </div>
    </div>
  );
}

// Convert a task row into the shape ReminderForm expects in its `initial` prop.
const taskToFormInitial = (task) => {
  if (!task) return undefined;
  const m = moment(task.due_at);
  return {
    uuid: task.uuid,
    description: task.description || '',
    due_date: m.isValid() ? m.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
    due_time: m.isValid() ? m.format('HH:mm') : moment().add(1, 'hour').format('HH:mm'),
    has_time: !!task.has_time,
    priority: task.priority || 'Medium',
    assignee_uuids: Array.isArray(task.assignee_uuids) ? task.assignee_uuids : [],
    linked_user_uuid: task.linked_user_uuid || '',
    label_uuids: task.label_uuids || [],
    alert_one: task.alerts?.[0]?.offset_minutes ?? '',
    alert_two: task.alerts?.[1]?.offset_minutes ?? '',
    attachments: Array.isArray(task.attachments) ? task.attachments : [],
    department_uuid: task.department_uuid || ''
  };
};

function RemindersModal({
  show,
  close,
  linked_user, // { uuid, name, type, role, iana_timezone }
  // Optional: override the default assignee (else the current operator) and
  // open the create form pre-filled — used when launching from a message.
  defaultAssigneeUuid,
  initialDescription,
  openFormOnMount
}) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  // 'all' | 'upcoming' | 'past' — drives the pills above the table.
  const [tableFilter, setTableFilter] = useState('all');
  // editingTask → form has a uuid, so submit = update. When null, the form
  // pane (if visible via `showForm`) creates a new task.
  const [editingTask, setEditingTask] = useState(null);
  // Right-hand form pane is hidden by default; the "+ New reminder" button
  // or the row's Edit icon flips this on. Editing also implies showForm.
  // openFormOnMount opens it straight away (launched from a message).
  const [showForm, setShowForm] = useState(!!openFormOnMount);
  // Notes-view modal (re-uses the dashboard's details modal).
  const [viewingTask, setViewingTask] = useState(null);
  // Task pending delete-confirmation (null when no confirm dialog is open).
  const [taskPendingDelete, setTaskPendingDelete] = useState(null);
  const cms_email = getCookie('cms_email');
  const me = useMemo(() => current_team_member(cms_email), [cms_email]);

  const refresh = useCallback(() => {
    if (!linked_user?.uuid) return;
    setLoading(true);
    list_tasks({ linked_user_uuid: linked_user.uuid })
      .then(({ tasks: rows }) => setTasks(rows))
      .catch((err) => {
        // Surface in console so failures are debuggable but don't leave the
        // spinner stuck or produce an unhandled promise rejection.
        // eslint-disable-next-line no-console
        console.error('reminders: list_tasks failed', err);
      })
      .finally(() => setLoading(false));
  }, [linked_user?.uuid]);

  useEffect(() => {
    if (show) {
      // Lazily prime the team-members cache the first time a profile opens
      // the reminders modal. Previously primed at module import, which fired
      // on every page that transitively pulled this file in.
      prime_team_members();
      refresh();
    } else {
      setEditingTask(null);
      setShowForm(false);
      setViewingTask(null);
    }
  }, [show, refresh]);

  const { upcoming, past } = useMemo(() => splitTasksUpcomingPast(tasks), [tasks]);
  // Rows visible in the table for the currently-selected pill. Upcoming asc,
  // Past desc, All combines both with upcoming first.
  const visibleTasks = useMemo(() => {
    if (tableFilter === 'upcoming') return upcoming;
    if (tableFilter === 'past') return past;
    return [...upcoming, ...past];
  }, [tableFilter, upcoming, past]);
  // uuid → task lookup powers the rich title tooltip (react-tooltip's render
  // prop only sees the data-tooltip-content string, so we resolve it here).
  const tasksByUuid = useMemo(() => Object.fromEntries(tasks.map((t) => [t.uuid, t])), [tasks]);
  // Tooltip timezone preference: the linked user's tz (the modal is scoped
  // to one profile, so showing times in their tz reads naturally and matches
  // the FaGlobe indicator in the modal header), then the CMS-wide operator
  // pref, then a best-guess fallback.
  const operatorTz = useSelector((s) => s.home?.iana_timezone);
  const tooltipTz = linked_user?.iana_timezone || operatorTz || moment.tz.guess();

  // Single handler for both create and update — the presence of `payload.uuid`
  // (carried through ReminderForm) tells us which mode we're in. Either way,
  // the form pane is dismissed after a successful submit.
  const handleSubmit = (payload) => {
    if (payload.uuid) {
      const { uuid, ...patch } = payload;
      return update_task(uuid, patch, me?.name).then(() => {
        setEditingTask(null);
        setShowForm(false);
        refresh();
      });
    }
    return create_task(payload, me?.name).then(() => {
      setShowForm(false);
      refresh();
    });
  };

  const handleToggleComplete = (task) =>
    update_task(
      task.uuid,
      { status: task.status === 'completed' ? 'pending' : 'completed' },
      me?.name
    ).then(() => refresh());

  const handleStartEdit = (task) => {
    setEditingTask(task);
    setShowForm(true);
  };
  const handleStartNew = () => {
    setEditingTask(null);
    setShowForm(true);
  };
  const handleCancelEdit = () => {
    setEditingTask(null);
    setShowForm(false);
  };

  const handleViewNotes = (task) => setViewingTask(task);

  // Stage a delete request; the actual delete fires when the user confirms in
  // the in-app ConfirmationPopup below (avoids native window.confirm per the
  // codebase guideline in .github/copilot-instructions.md).
  const handleDelete = (task) => setTaskPendingDelete(task);

  const confirmDelete = () => {
    const task = taskPendingDelete;
    if (!task) return;
    delete_task(task.uuid).then(() => {
      // If we were editing or viewing the deleted row, clean those up too.
      if (editingTask?.uuid === task.uuid) {
        setEditingTask(null);
        setShowForm(false);
      }
      if (viewingTask?.uuid === task.uuid) setViewingTask(null);
      setTaskPendingDelete(null);
      refresh();
    });
  };

  if (!linked_user) return null;

  return (
    <CustomModal
      show={show}
      close={close}
      showCloseBtn
      customClose
      inner_style={{
        width: 'min(1100px, 96vw)',
        maxWidth: 'none',
        maxHeight: '90vh',
        padding: '0',
        overflow: 'hidden',
        borderRadius: '14px'
      }}
    >
      <div className="reminders-scope flex h-[80vh] max-h-[720px] flex-col bg-white">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-mytPrimary/10 text-mytPrimary">
              <FaRegBell size={14} />
            </div>
            <div>
              <div className="text-base font-semibold text-gray-800">Reminders</div>
              <div className="text-xs text-gray-500">
                {linked_user.name}
                <span className="mx-1.5 text-gray-300">·</span>
                <span className="uppercase tracking-wider">{linked_user.type}</span>
                {linked_user.iana_timezone ? (
                  <>
                    <span className="mx-1.5 text-gray-300">·</span>
                    <span className="inline-flex items-center gap-1">
                      <FaGlobe size={9} /> {linked_user.iana_timezone}
                      <span className="text-gray-400">
                        ({moment.tz(linked_user.iana_timezone).format('h:mm A')} local)
                      </span>
                    </span>
                  </>
                ) : null}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            title="Close"
            className="reminders-close-btn inline-flex h-8 w-8 items-center justify-center"
          >
            <FaTimes size={12} />
          </button>
        </div>

        {/* Body */}
        <div className="grid min-h-0 flex-1 grid-cols-12 gap-0">
          {/*
            Left — task table. Spans the full width when the form pane is
            hidden, falls back to 7/12 when the form opens so the right pane
            has room. Border-right only when the form is showing — looks
            cleaner when the left pane is alone.
          */}
          <div
            className={`col-span-12 flex min-h-0 flex-col ${
              showForm ? 'border-r border-gray-200 md:col-span-7' : ''
            }`}
          >
            {/* Pills filter row + "+ New reminder" CTA on the right. */}
            <div className="flex items-center justify-between gap-1.5 border-b border-gray-200 px-3 py-2">
              <div className="flex items-center gap-1.5">
                {[
                  { value: 'all', label: 'All', count: tasks.length },
                  { value: 'upcoming', label: 'Upcoming', count: upcoming.length },
                  { value: 'past', label: 'Past', count: past.length }
                ].map((pill) => {
                  const active = tableFilter === pill.value;
                  return (
                    <button
                      key={pill.value}
                      type="button"
                      onClick={() => setTableFilter(pill.value)}
                      className={`reminders-pill rounded-full px-3 py-1 text-xs transition ${
                        active ? 'reminders-pill-active' : ''
                      }`}
                    >
                      {pill.label} ({pill.count})
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={handleStartNew}
                className="inline-flex items-center gap-1.5 rounded-full bg-mytPrimary px-3 py-1 text-xs font-medium text-white transition hover:opacity-90"
              >
                <FaPlus size={10} /> New reminder
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading && visibleTasks.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-gray-400">Loading…</div>
              ) : visibleTasks.length === 0 ? (
                <div className="px-3 py-10 text-center text-xs text-gray-400">
                  {tableFilter === 'upcoming'
                    ? 'No upcoming reminders.'
                    : tableFilter === 'past'
                      ? 'No past reminders.'
                      : 'No reminders yet.'}
                </div>
              ) : (
                visibleTasks.map((t) => (
                  <TaskRow
                    key={t.uuid}
                    task={t}
                    onToggleComplete={handleToggleComplete}
                    onEdit={handleStartEdit}
                    onViewNotes={handleViewNotes}
                    onDelete={handleDelete}
                    isEditing={editingTask?.uuid === t.uuid}
                  />
                ))
              )}
            </div>
          </div>

          {/*
            Right — create / edit form. Hidden by default; revealed when the
            operator clicks "+ New reminder" or the row's Edit icon. Cancel
            and successful submit both collapse it again.
          */}
          {showForm ? (
            <div className="col-span-12 flex min-h-0 flex-col bg-gray-50/40 md:col-span-5">
              {/*
                Heading row sits outside the scrolling region so "New reminder"
                + Cancel stay anchored at the top while the form body scrolls.
                Matches the modal header's border-b / py-3 padding for visual
                continuity.
              */}
              <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50/40 px-5 py-3">
                <div className="text-sm font-semibold text-gray-700">
                  {editingTask ? 'Edit reminder' : 'New reminder'}
                </div>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  data-tooltip-id="reminders-tooltip"
                  data-tooltip-content={
                    editingTask ? 'Stop editing' : 'Close the new-reminder form'
                  }
                  className="reminders-icon-btn inline-flex items-center gap-1 text-xs"
                >
                  <FaTimes size={9} /> Cancel
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4">
                <ReminderForm
                  // Remount when switching seeds so internal state resets cleanly.
                  key={editingTask?.uuid || 'new'}
                  initial={
                    editingTask
                      ? taskToFormInitial(editingTask)
                      : initialDescription
                        ? { description: initialDescription }
                        : undefined
                  }
                  lockedLinkedUser={linked_user}
                  defaultAssigneeUuid={defaultAssigneeUuid || me?.uuid}
                  selfUuid={me?.uuid}
                  onSubmit={handleSubmit}
                  onCancel={handleCancelEdit}
                  submitLabel={editingTask ? 'Save' : 'Create'}
                />
                {/* Editing an existing reminder also surfaces its notes thread,
                    matching the details modal. Notes attach to a saved
                    reminder, so this is hidden when creating a new one. */}
                {editingTask ? (
                  <div className="mt-4 flex min-h-0 flex-col border-t border-gray-200 pt-4">
                    <ReminderNotes
                      taskUuid={editingTask.uuid}
                      notes={editingTask.notes}
                      onUpdated={(updated) => {
                        setEditingTask(updated);
                        refresh();
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
        {/*
          Local tooltip instance. The app-root tooltip in App.jsx uses
          z-index 100 which sits below the CustomModal, so hovers inside
          the modal would render the tooltip behind it. Mounting our own
          instance inside the modal lets us bump z-index above the modal
          and still inherit the simple data-attribute API.
        */}
        <ReactTooltip
          id="reminders-tooltip"
          place="top"
          style={{ zIndex: 100000, fontSize: 11, padding: '4px 8px' }}
        />
        {/*
          Rich quick-description tooltip for the task title — same content
          as the /reminders dashboard's title hover, so operators see Due /
          Alerts / Description without opening the row. Uses a separate id
          from the action-button tooltip above so the small-hint and rich-
          card visuals stay isolated. zIndex 100001 keeps it above the
          modal-scope tooltip in case both could appear stacked.
        */}
        <ReactTooltip
          id="reminders-task-tooltip"
          place="top"
          delayShow={150}
          delayHide={60}
          render={({ content }) => {
            const t = tasksByUuid[content];
            return t ? <TaskTooltipContent task={t} tz={tooltipTz} /> : null;
          }}
          style={{ ...taskTooltipStyle, zIndex: 100001 }}
          opacity={1}
        />
      </div>
      {/* Notes/details modal — stacks on top of the profile reminders modal. */}
      <ReminderDetailsModal
        show={!!viewingTask}
        close={() => setViewingTask(null)}
        task={viewingTask}
        onUpdated={(updated) => {
          setViewingTask(updated);
          refresh();
        }}
      />
      {/* In-app delete confirmation, replaces the prior window.confirm call. */}
      <CustomModal show={!!taskPendingDelete} close={() => setTaskPendingDelete(null)}>
        <ConfirmationPopup
          title={
            taskPendingDelete ? `Delete "${taskPendingDelete.title}"? This can't be undone.` : ''
          }
          close_modal={() => setTaskPendingDelete(null)}
          confirm={confirmDelete}
        />
      </CustomModal>
    </CustomModal>
  );
}

export default RemindersModal;
